import { fromUnixTime } from "date-fns";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { MOBILE_BREAKPOINT } from "../../../config";
import {
  CreatorNotesQuery,
  CreatorNotesScope,
  useCreateCreatorNoteMutation,
  useCreatorNotesQuery,
  useDeleteCreatorNoteMutation,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { authSelectors } from "../../../store/auth/selector";
import styled from "../../../styles";
import { formatDateTime } from "../../../utils/formatTime";
import { InitialsAvatar } from "../../Avatar";
import { Card } from "../../Card";
import { Button, ButtonWrap } from "../../CTA";
import { CardDivider } from "../../Divider";
import { Dropdown } from "../../Dropdown";
import { Flex } from "../../Flex";
import { InlineAddBtn } from "../../NewButton";
import { SegmentedControl } from "../../SegmentedControl";
import { Tag } from "../../Tag";
import { Text } from "../../Text";
import { TextArea } from "../../TextArea";
import { Tooltip } from "../../Tooltip";
import { View } from "../../View";
import { StackOnMobile } from "../ContactDetails";

const CreatorNotes = ({ creatorId }: { creatorId: string }) => {
  const [queryScope, setQueryScope] = useState<CreatorNotesScope>(
    CreatorNotesScope.BrandOnly
  );
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [message, setMessage] = useState("");

  const account = useSelector(authSelectors.account);
  const brand = useSelector(authSelectors.activeBrand);
  const client = useGqlClient();

  const { mutate: createNote } = useCreateCreatorNoteMutation(client);
  const { mutate: deleteNote } = useDeleteCreatorNoteMutation(client);
  const hasMultipleBrands = account?.brands && account.brands.length > 1;

  // Check if user has access to the current brand's group
  let hasGroupAccess = false;
  if (brand?.brandGroup && account?.brandGroups) {
    hasGroupAccess = account.brandGroups.some(
      (group) => group.id === brand.brandGroup?.id
    );
  }

  // Check if all brands are part of the same group as the current brand
  let allBrandsInSameGroup = false;
  if (hasGroupAccess && account?.brands && account.brands.length > 0) {
    allBrandsInSameGroup = account.brands.every(
      (accountBrand) => accountBrand.brandGroup?.id === brand?.brandGroup?.id
    );
  }

  const segmentedOptions = [];
  let maxWidth = 140;

  function shortenName(name: string) {
    if (name.length > 18) {
      return name.slice(0, 18) + "...";
    }
    return name;
  }

  // Only build segmented options if user has multiple brands
  if (hasMultipleBrands) {
    // Always add the current brand option
    segmentedOptions.push({
      label: shortenName(brand?.name || "Current Brand"),
      value: CreatorNotesScope.BrandOnly,
    });

    // Add group option if user has group access
    if (hasGroupAccess) {
      segmentedOptions.push({
        label: shortenName(brand?.brandGroup?.name || "Group"),
        value: CreatorNotesScope.BrandGroup,
      });
      maxWidth += 140;
    }

    // Add "All Brands" option only if not all brands are in the same group
    if (!allBrandsInSameGroup) {
      segmentedOptions.push({
        label: "All Brands",
        value: CreatorNotesScope.AllBrands,
      });
      maxWidth += 140;
    }
  }

  const { data, refetch } = useCreatorNotesQuery(client, {
    creatorId: creatorId,
    scope: hasMultipleBrands ? queryScope : CreatorNotesScope.BrandOnly,
  });

  const showSegmentedControl = segmentedOptions.length > 1;

  const handleSendMessage = () => {
    createNote(
      {
        input: {
          creatorId: creatorId,
          note: message,
        },
      },
      {
        onSuccess: () => {
          setIsAddingNote(false);
          setMessage("");
          refetch();
          toast.success("Note added");
        },
      }
    );
  };

  return (
    <NotesSection>
      <StackOnMobile>
        <Flex align="center">
          <Text margin="0" size="l" weight="bold" colorPreset="heading">
            Internal Notes
          </Text>
          <div style={{ marginBottom: -1, marginLeft: 6 }}>
            <Tooltip manualPosition="below">
              <Text size="s" margin="0">
                Visible to all users with access to this brand
              </Text>
            </Tooltip>
          </div>
        </Flex>
        {showSegmentedControl && (
          <SegmentedControlWrap maxWidth={maxWidth}>
            <SegmentedControl
              onChange={(e: CreatorNotesScope) => setQueryScope(e)}
              value={queryScope}
              options={segmentedOptions}
            />
          </SegmentedControlWrap>
        )}
      </StackOnMobile>
      {data?.creatorNotes.map((note: CreatorNotesQuery["creatorNotes"][0]) => (
        <Card padding="l l m" margin="l 0">
          <Flex justify="space-between" align="flex-start">
            <Flex direction="row" align="flex-start">
              <View margin="0">
                <Flex align="center">
                  <InitialsAvatar
                    name={`${note.createdBy?.firstName || ""} ${
                      note.createdBy?.lastName || ""
                    }`}
                    size={28}
                  />
                  <div style={{ marginBottom: -2 }}>
                    <Text size="xs" margin="0 xs 0 s" weight="semi">
                      {note.createdBy?.firstName} {note.createdBy?.lastName}
                    </Text>
                  </div>
                  <div style={{ marginBottom: 2 }}>
                    <Text
                      size="xxs"
                      margin="0"
                      isInline
                      colorPreset="secondary"
                    >
                      {" · "} {formatDateTime(fromUnixTime(note.createdAt))}
                    </Text>
                  </div>
                  <DesktopOnly>
                    {note.brand &&
                      queryScope !== CreatorNotesScope.BrandOnly && (
                        <View margin="0 0 0 s" style={{ marginTop: -3 }}>
                          <Tag text={note.brand.name} color="blue" size="s" />
                        </View>
                      )}
                  </DesktopOnly>
                </Flex>
                <Text margin="s xxs 0">{note.note}</Text>
                <MobileOnly>
                  {note.brand && queryScope !== CreatorNotesScope.BrandOnly && (
                    <View margin="0 0 0 0">
                      <Tag text={note.brand.name} color="blue" size="s" />
                    </View>
                  )}
                </MobileOnly>
              </View>
            </Flex>
            <Flex>
              {note && note.createdBy?.id === account?.id ? (
                <Dropdown
                  dropdownType="actions"
                  selectionMode="single"
                  renderLabel={() => {
                    return "Actions";
                  }}
                  anchor="top end"
                  selectedOptions={[]}
                  setSelectedOptions={(selectedOptions) => {
                    const selectedValue = selectedOptions[0];

                    if (selectedValue === "delete") {
                      deleteNote(
                        {
                          id: note.id,
                        },
                        {
                          onSuccess: () => {
                            toast.success("Note deleted");
                            refetch();
                          },
                        }
                      );
                    }
                  }}
                  options={[
                    {
                      label: "Delete",
                      value: "delete",
                    },
                  ]}
                />
              ) : null}
            </Flex>
          </Flex>
        </Card>
      ))}
      {isAddingNote && (
        <View margin="s 0 0">
          {data && data.creatorNotes.length && data.creatorNotes.length > 0 ? (
            <CardDivider margin="xs 0 l" />
          ) : null}
          <AreaWrap>
            <TextArea
              placeholder="Leave a message..."
              value={message}
              rows={3}
              autoFocus={true}
              onChange={(e) => setMessage(e.target.value)}
            />
            <ButtonWrap style={{ maxWidth: 320 }}>
              <Button size="xs" onClick={handleSendMessage}>
                Add
              </Button>
              <Button
                buttonType="secondary"
                size="xs"
                onClick={() => setIsAddingNote(false)}
              >
                Cancel
              </Button>
            </ButtonWrap>
          </AreaWrap>
        </View>
      )}
      {!isAddingNote && (
        <View margin="s 0 0">
          <InlineAddBtn
            label="Add Note"
            onClick={() => setIsAddingNote(true)}
          />
        </View>
      )}
    </NotesSection>
  );
};

export default CreatorNotes;

const NotesSection = styled.div`
  background-color: ${(p) => p.theme.color.card.callout};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  width: 100%;
  border: 0px solid ${(p) => p.theme.color.card.divider};
  padding: ${(p) => p.theme.spacing.l} ${(p) => p.theme.spacing.xl};
  box-sizing: border-box;
  overflow-y: visible;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(p) => p.theme.spacing.m} ${(p) => p.theme.spacing.m}
      ${(p) => p.theme.spacing.s};
  }
`;

const AreaWrap = styled.div`
  position: relative;
`;

const SegmentedControlWrap = styled.div<{ maxWidth: number }>`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    margin-top: ${(p) => p.theme.spacing.s};
  }
  box-sizing: border-box;
  width: 100%;
  max-width: ${(p) => p.maxWidth}px;
`;

const DesktopOnly = styled.div`
  display: block;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;
